<script setup>
import BaseIcon from "@/Components/UI/BaseIcon.vue";
import cn from "@/helpers/cn.js";
import {ref} from "vue";

defineProps({
    certificate: {
        type: Object,
        required: true,
    },
    full: Boolean,
    selected: Boolean,
})

const root = ref(null);

// Enter/Space must invoke the same handler a click does. The parent attaches
// its @click as a native listener via attrs fallthrough (no "click" emit is
// declared here), so re-dispatching through the element itself reuses that
// exact listener instead of duplicating the selection logic.
const activateByKeyboard = () => root.value?.click();

// Two things about the root element below, neither obvious from reading it:
//
// `data-clarity-mask` has to sit on the click target, not only on the spans inside it.
// Clarity reports the text of the CLICKED element, and this row is what people click — with
// the mask on the inner spans alone, organization names and the start of surnames were
// reaching Clarity's clicked-text (98 hits in a 6-day sample).
//
// The selected state carries a FILL, not just a border colour. Without it the chosen row was
// quieter than a merely hovered one (which gets bg-blue-5): moving the mouse away left nothing
// but 1px of border, and half the recorded clicks on this list were people re-picking what
// they had already picked.
</script>

<template>
    <div ref="root"
         role="radio"
         data-clarity-mask="True"
         :aria-checked="selected"
         tabindex="0"
         :class="cn(
             'p-10 rounded border flex gap-5 transition-colors',
             certificate.invalid
                 ? 'grayscale cursor-not-allowed border-light-2'
                 : selected
                     ? 'border-accent-blue bg-blue-5 cursor-default'
                     : 'cursor-pointer border-light-2 hover:border-light-1 hover:bg-blue-5',
         )"
         @keydown.enter="activateByKeyboard"
         @keydown.space.prevent="activateByKeyboard">
        <img alt=""
             class="h-16"
             src="/storage/images/certificate.svg">
        <div>
            <h4 class="font-semibold my-0"><span data-clarity-mask="True">{{ certificate.subject.CN }}</span></h4>
            <p v-if="certificate.subject.SN && certificate.subject.G"
               class="text-dark-light capitalize mt-1 mb-0">
                <span data-clarity-mask="True">
                    {{ certificate.subject.SN.toLowerCase() }}
                    {{ certificate.subject.G.toLowerCase() }}
                </span>
            </p>
            <p v-else class="text-dark-light mt-1 mb-0">
                <span data-clarity-mask="True">{{ certificate.subject.E }}</span>
            </p>
            <div v-if="full" class="text-xs">
                <p class="text-dark-light mb-0">ИНН: <span data-clarity-mask="True">{{ certificate.subject['ИНН'] }}</span></p>
                <p class="text-dark-light mb-0">СНИЛС: <span data-clarity-mask="True">{{ certificate.subject['СНИЛС'] }}</span></p>
            </div>
            <p class="text-dark-light text-xs mb-0">
                Действителен до <span data-clarity-mask="True">{{ new Date(certificate.validTo).toLocaleDateString() }}</span>
            </p>
        </div>
        <BaseIcon v-if="selected"
                  name="check"
                  class="ml-auto self-center w-10 h-10 text-accent-blue shrink-0" />
    </div>
</template>
